import { EnvironmentInjector, inject } from '@angular/core'
import { signalStore, withMethods } from '@ngrx/signals'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { clearStorageByPrefix } from '@core/utils/main'
import { OidcService } from '@core/authentication/oidc/oidc.service'
import { OAuthErrorCode } from '@core/authentication/auth/auth.enums'
import { ProfileStore } from '@core/store/profile/profile.store'
import { TenantStore } from '@core/store/tenant/tenant.store'
import { RouterStore } from '@core/router/router.store'
import { User } from 'oidc-client-ts'
import { firstValueFrom, iif, of } from 'rxjs'
import { catchError, switchMap, tap } from 'rxjs/operators'
import { environment } from '@env'

/**
 * DI note: `ProfileStore` injects `OidcStore` (its `fetchUserProfile`
 * 204/error paths call `logOut` / `navigateExternal`), so this store
 * resolves `ProfileStore` **lazily** through the `EnvironmentInjector`
 * inside `logOut()` instead of injecting it in the factory — a direct
 * `inject(ProfileStore)` here would be a construction-time circular
 * dependency.
 */
export const OidcStore = signalStore(
  { providedIn: 'root' },
  withMethods(() => {
    const manager = inject(OidcService)
    const routerStore = inject(RouterStore)
    const tenantStore = inject(TenantStore)
    const injector = inject(EnvironmentInjector)
    const getProfileStore = () => injector.get(ProfileStore)

    const handleSignInError = (err: any) => {
      console.warn('[OIDC] Redirecting', err.message)
      clearStorageByPrefix(localStorage, 'oidc.')
      return manager.removeUser().pipe(tap(() => (window.location.href = window.location.origin)))
    }

    return {
      signInRedirect: rxMethod<string | undefined>((source$) =>
        source$.pipe(
          switchMap((state) =>
            manager.signInRedirect({ state }).pipe(catchError((e) => handleSignInError(e))),
          ),
        ),
      ),
      signInRedirectCallback: rxMethod<void>((source$) =>
        source$.pipe(
          switchMap(() =>
            manager.signInRedirectCallback().pipe(
              catchError((err) =>
                iif(
                  () => err === OAuthErrorCode.AccessDenied,
                  of(undefined).pipe(
                    tap(() =>
                      routerStore.navigateExternal(
                        environment.IDENTITY_SERVER.POST_LOGOUT_REDIRECT_URI,
                      ),
                    ),
                  ),
                  handleSignInError(err),
                ),
              ),
              tap((oidc: User) =>
                routerStore.navigate(oidc.state || environment.REDIRECT_LOGGED_URL),
              ),
            ),
          ),
        ),
      ),
      logOut(uri: string = environment.IDENTITY_SERVER.POST_LOGOUT_REDIRECT_URI): void {
        const profileStore = getProfileStore()
        const idToken = profileStore.oidcTokenId()
        clearStorageByPrefix(localStorage, 'oidc.')
        void firstValueFrom(manager.removeUser()).then(
          () => {
            window.location.href =
              environment.IDENTITY_SERVER.API_LOGOUT +
              '?id_token_hint=' +
              idToken +
              '&' +
              environment.IDENTITY_SERVER.TAG_POST_LOGOUT_REDIRECT_URI +
              '=' +
              uri
            profileStore.purgeUserProfile()
          },
          (err) => console.error('Unable to remove the OIDC user', err),
        )
      },
      generateNewIDSUserUrl(extraParams?: string[]): void {
        const tenantId = tenantStore.selectedTenantId()
        const url = `${environment.IDENTITY_SERVER.REGISTER_ACCOUNT_URL}?tenantId=${tenantId}${
          (extraParams || []).length > 0 ? `&${extraParams.join('&')}` : ''
        }`
        routerStore.navigateToNewTab(url)
      },
    }
  }),
)
